import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { ColumnDef } from '@tanstack/react-table';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ExtraWorkOrder } from '../models';
import { Button } from '@shared/components/ui/button';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/pro-light-svg-icons';
import { getExtraWorkOrderStatusConfig } from '../config/extraWorkOrderStatusConfig';

interface UseExtraWorkOrdersAgGridColumnsProps {
  selectedColumns?: string[];
}

const formatDate = (date?: string | null) => {
  if (!date) {
    return '-';
  }

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return format(parsed, 'dd/MM/yyyy');
};

const formatDateTime = (date?: string | null) => {
  if (!date) {
    return '-';
  }

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return format(parsed, 'dd/MM/yyyy HH:mm');
};

const convertToTanStackColumns = (agGridColumns: ColDef[]): ColumnDef<ExtraWorkOrder>[] =>
  agGridColumns
    .filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return field !== 'actions' && field !== 'unknown';
    })
    .map((column) => ({
      id: column.field || column.headerName || 'unknown',
      accessorKey: column.field || column.headerName || 'unknown',
      header: column.headerName || 'Columna',
    }));

export const useExtraWorkOrdersAgGridColumns = ({ selectedColumns = [] }: UseExtraWorkOrdersAgGridColumnsProps) => {
  const { t } = useTranslation('extraWorkOrdersModule');
  const navigate = useNavigate();

  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      {
        field: 'ref',
        headerName: t('table.ref', 'Referencia'),
        sortable: true,
        minWidth: 150,
        cellRenderer: (params: any) => {
          const order = params.data as ExtraWorkOrder;
          return <span className="text-sm font-medium text-text">{order.ref}</span>;
        },
      },
      {
        field: 'status',
        headerName: t('table.status', 'Estado'),
        sortable: false, // No disponible en API para ordenamiento
        minWidth: 150,
        cellRenderer: (params: any) => {
          const order = params.data as ExtraWorkOrder;
          const statusConfig = getExtraWorkOrderStatusConfig(order.status);
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}
            >
              {t(`status.${order.status}`, statusConfig.label)}
            </span>
          );
        },
      },
      {
        field: 'deadline_at',
        headerName: t('table.deadline', 'Fecha límite'),
        sortable: false, // No disponible en API para ordenamiento
        minWidth: 180,
        valueGetter: (params) => params.data?.deadline_at,
        cellRenderer: (params: any) => {
          const order = params.data as ExtraWorkOrder;
          return <span className="text-sm text-text">{order.deadline_at ? formatDate(order.deadline_at) : '-'}</span>;
        },
      },
      {
        field: 'merchandise',
        headerName: t('table.merchandise', 'Mercancía'),
        sortable: false,
        minWidth: 200,
        valueGetter: (params) => {
          const merchandise = params.data?.merchandise;
          return merchandise?.name || merchandise?.ref || params.data?.merchandise_id || '-';
        },
        cellRenderer: (params: any) => {
          const order = params.data as ExtraWorkOrder;
          return (
            <span className="text-sm text-text">
              {order.merchandise?.name || order.merchandise?.ref || order.merchandise_id || '-'}
            </span>
          );
        },
      },
      {
        field: 'location',
        headerName: t('table.location', 'Ubicación origen'),
        sortable: false,
        minWidth: 200,
        valueGetter: (params) => {
          const location = params.data?.location;
          return location?.code || location?.name || params.data?.location_id || '-';
        },
        cellRenderer: (params: any) => {
          const order = params.data as ExtraWorkOrder;
          return (
            <span className="text-sm text-text">
              {order.location?.code || order.location?.name || order.location_id || '-'}
            </span>
          );
        },
      },
      {
        field: 'progress',
        headerName: t('table.progress', 'Progreso'),
        sortable: false,
        minWidth: 150,
        valueGetter: (params) => {
          const lines = params.data?.lines || [];
          const doneLines = lines.filter((l: any) => l.status === 'DONE').length;
          return `${doneLines}/${lines.length}`;
        },
        cellRenderer: (params: any) => {
          const order = params.data as ExtraWorkOrder;
          const lines = order.lines || [];
          const doneLines = lines.filter((l) => l.status === 'DONE').length;
          const totalLines = lines.length;
          return <span className="text-sm text-text">{totalLines > 0 ? `${doneLines}/${totalLines}` : '-'}</span>;
        },
      },
      {
        field: 'created_at',
        headerName: t('table.created_at', 'Fecha creación'),
        sortable: true,
        minWidth: 180,
        valueGetter: (params) => params.data?.created_at,
        cellRenderer: (params: any) => {
          const order = params.data as ExtraWorkOrder;
          return <span className="text-sm text-text">{order.created_at ? formatDateTime(order.created_at) : '-'}</span>;
        },
      },
    ];

    baseColumns.push({
      field: 'actions',
      headerName: '',
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      resizable: false,
      pinned: 'right',
      lockPosition: 'right',
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      cellRenderer: (params: any) => {
        const order = params.data as ExtraWorkOrder;
        const handleClick = (e: React.MouseEvent) => {
          // Si se presiona Ctrl o Cmd, abrir en nueva pestaña
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            window.open(`/extra-work-orders/${order.id}`, '_blank');
          } else {
            // Click normal, navegar en la misma pestaña
            navigate(`/extra-work-orders/${order.id}`);
          }
        };

        return (
          <div className="flex justify-center w-full">
            <Button
              variant="outline"
              size="sm"
              onClick={handleClick}
              data-testid={`view-order-${order.id}`}
              className="h-8 w-8 p-0"
              title={t('actions.view', 'Ver detalle')}
            >
              <FontAwesomeIcon icon={faEye} className="w-3.5 h-3.5" />
            </Button>
          </div>
        );
      },
    });

    return baseColumns;
  }, [t, navigate]);

  const filteredColumnDefs = useMemo(() => {
    const alwaysVisible = allColumnDefs.filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return field === 'actions' || field === 'ref';
    });

    const selectableColumns = allColumnDefs.filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return field !== 'actions';
    });

    if (!selectedColumns.length) {
      return [...selectableColumns, ...alwaysVisible].reduce<ColDef[]>((acc, column) => {
        const field = column.field || column.headerName || 'unknown';
        if (!acc.some((existing) => (existing.field || existing.headerName || 'unknown') === field)) {
          acc.push(column);
        }
        return acc;
      }, []);
    }

    const selectedDefs = selectableColumns.filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return selectedColumns.includes(field);
    });

    const combined = [...selectedDefs];
    alwaysVisible.forEach((column) => {
      const field = column.field || column.headerName || 'unknown';
      if (!combined.some((existing) => (existing.field || existing.headerName || 'unknown') === field)) {
        combined.push(column);
      }
    });

    return combined;
  }, [allColumnDefs, selectedColumns]);

  const tanStackColumns = useMemo(() => convertToTanStackColumns(allColumnDefs), [allColumnDefs]);

  return {
    allColumnDefs,
    filteredColumnDefs,
    tanStackColumns,
  };
};
